// ============================================
// DIAGNOSTICTEMPLATEFORMMODAL.JSX - Create/Edit Diagnostic Template Form
// ============================================
// Modal form for creating and editing diagnostic form templates with inline questions

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  useCreateDiagnosticTemplateMutation,
  useUpdateDiagnosticTemplateMutation,
  usePublishDiagnosticTemplateMutation,
  useGetActionsForCompanyQuery,
} from "../../../services/api";
import "./GlobalTemplates.css";

// ============================================
// SVG ICONS
// ============================================
const IconClose = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// ============================================
// CONSTANTS
// ============================================
const QUESTION_TYPES = [
  { value: "text_short", label: "Short Text" },
  { value: "text_long", label: "Long Text" },
  { value: "single_choice", label: "Single Choice" },
  { value: "multi_choice", label: "Multiple Choice" },
  { value: "rating_scale", label: "Rating Scale" },
];

const VALID_QUESTION_TYPE_VALUES = QUESTION_TYPES.map((t) => t.value);

// ============================================
// ACTION PICKER COMPONENT (custom dropdown)
// ============================================
const IconChevron = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

function ActionPicker({ value, actions, groupedActions, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const selectedAction = value ? actions.find((a) => a.actionId === value) : null;

  // Filter actions by search
  const filteredGrouped = useMemo(() => {
    if (!search.trim()) return groupedActions;
    const q = search.toLowerCase();
    const result = {};
    Object.entries(groupedActions).forEach(([valueName, competencies]) => {
      const filteredComps = {};
      Object.entries(competencies).forEach(([compName, compActions]) => {
        const matched = compActions.filter(
          (a) =>
            a.actionName.toLowerCase().includes(q) ||
            compName.toLowerCase().includes(q) ||
            valueName.toLowerCase().includes(q)
        );
        if (matched.length) filteredComps[compName] = matched;
      });
      if (Object.keys(filteredComps).length) result[valueName] = filteredComps;
    });
    return result;
  }, [groupedActions, search]);

  const hasResults = Object.keys(filteredGrouped).length > 0;

  return (
    <div className="action-picker" ref={ref}>
      <button
        type="button"
        className={`action-picker-trigger ${open ? "open" : ""}`}
        onClick={() => { setOpen(!open); setSearch(""); }}
      >
        {selectedAction ? (
          <div className="action-picker-selected">
            <span className="action-picker-value-tag">{selectedAction.valueName}</span>
            <span className="action-picker-comp-tag">{selectedAction.competencyName}</span>
            <span className="action-picker-action-text">{selectedAction.actionName}</span>
          </div>
        ) : (
          <span className="action-picker-placeholder">-- Select an Action --</span>
        )}
        <IconChevron open={open} />
      </button>

      {open && (
        <div className="action-picker-dropdown">
          <div className="action-picker-search">
            <IconSearch />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search actions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="action-picker-list">
            {selectedAction && (
              <button
                type="button"
                className="action-picker-clear"
                onClick={() => { onSelect(""); setOpen(false); }}
              >
                Clear selection
              </button>
            )}
            {hasResults ? (
              Object.entries(filteredGrouped).map(([valueName, competencies]) => (
                <div key={valueName} className="action-picker-group">
                  <div className="action-picker-group-header">{valueName}</div>
                  {Object.entries(competencies).map(([compName, compActions]) => (
                    <div key={compName} className="action-picker-subgroup">
                      <div className="action-picker-subgroup-header">{compName}</div>
                      {compActions.map((action) => (
                        <button
                          key={action.actionId}
                          type="button"
                          className={`action-picker-option ${value === action.actionId ? "selected" : ""}`}
                          onClick={() => { onSelect(action.actionId); setOpen(false); }}
                        >
                          <span className="action-picker-option-name">{action.actionName}</span>
                          {action.entries?.length > 0 && (
                            <span className="action-picker-option-meta">
                              {action.entries.length} {action.entries.length === 1 ? "entry" : "entries"}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="action-picker-empty">No actions match your search.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Normalize question type to a valid value
const normalizeQuestionType = (type) => {
  if (VALID_QUESTION_TYPE_VALUES.includes(type)) {
    return type;
  }
  const typeMap = {
    checkbox: "single_choice",
    radio: "single_choice",
    select: "single_choice",
    textarea: "text_long",
    input: "text_short",
  };
  return typeMap[type] || "text_short";
};


// ============================================
// QUESTION EDITOR COMPONENT
// ============================================
const QuestionEditor = ({ question, index, onChange, onRemove, actions = [], actionsLoading }) => {
  const needsOptions = ["single_choice", "multi_choice"].includes(question.questionType);
  const needsRatingConfig = question.questionType === "rating_scale";
  const needsActionSelect = question.questionType === "rating_scale";

  const handleChange = (field, value) => {
    onChange(index, { ...question, [field]: value });
  };

  // Handle action selection
  const handleActionSelect = (actionId) => {
    if (!actionId) {
      handleChange("linkedAction", null);
      return;
    }
    const selectedAction = actions.find((a) => a.actionId === actionId);
    if (selectedAction) {
      handleChange("linkedAction", {
        actionId: selectedAction.actionId,
        actionName: selectedAction.actionName,
        competencyName: selectedAction.competencyName,
        valueName: selectedAction.valueName,
        entries: selectedAction.entries || [],
      });
    } else {
      // Action no longer exists - clear it
      handleChange("linkedAction", null);
    }
  };

  // Group actions by value > competency for dropdown display
  const groupedActions = useMemo(() => {
    const grouped = {};
    actions.forEach((action) => {
      const valueKey = action.valueName || "Other";
      const compKey = action.competencyName || "General";
      if (!grouped[valueKey]) grouped[valueKey] = {};
      if (!grouped[valueKey][compKey]) grouped[valueKey][compKey] = [];
      grouped[valueKey][compKey].push(action);
    });
    return grouped;
  }, [actions]);

  const handleOptionChange = (optIndex, field, value) => {
    const newOptions = [...(question.options || [])];
    newOptions[optIndex] = { ...newOptions[optIndex], [field]: value };
    handleChange("options", newOptions);
  };

  const addOption = () => {
    const newOptions = [...(question.options || [])];
    newOptions.push({ label: "", value: "" });
    handleChange("options", newOptions);
  };

  const removeOption = (optIndex) => {
    const newOptions = [...(question.options || [])];
    newOptions.splice(optIndex, 1);
    handleChange("options", newOptions);
  };

  const handleRatingConfigChange = (field, value) => {
    const newConfig = { ...(question.ratingConfig || { min: 1, max: 5 }), [field]: value };
    handleChange("ratingConfig", newConfig);
  };

  return (
    <div className="question-editor">
      <div className="question-editor-header">
        <span className="question-number">Q{index + 1}</span>
        <button className="question-remove-btn" onClick={() => onRemove(index)} type="button">
          <IconTrash />
        </button>
      </div>

      <div className="question-editor-row">
        <div className="form-group type-select">
          <label>Type</label>
          <select
            value={question.questionType}
            onChange={(e) => handleChange("questionType", e.target.value)}
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group flex-1">
          <label>Question Text</label>
          <input
            type="text"
            placeholder="Enter your question..."
            value={question.questionText}
            onChange={(e) => handleChange("questionText", e.target.value)}
          />
        </div>

        <div className="form-group required-check">
          <label>
            <input
              type="checkbox"
              checked={question.isRequired || false}
              onChange={(e) => handleChange("isRequired", e.target.checked)}
            />
            Required
          </label>
        </div>
      </div>

      {/* Options for choice types */}
      {needsOptions && (
        <div className="question-options">
          <label className="options-label">Options</label>
          {(question.options || []).map((opt, optIndex) => (
            <div key={optIndex} className="option-row">
              <input
                type="text"
                placeholder="Label"
                value={opt.label}
                onChange={(e) => handleOptionChange(optIndex, "label", e.target.value)}
              />
              <input
                type="text"
                placeholder="Value"
                value={opt.value}
                onChange={(e) => handleOptionChange(optIndex, "value", e.target.value)}
              />
              <button
                type="button"
                className="option-remove-btn"
                onClick={() => removeOption(optIndex)}
              >
                <IconTrash />
              </button>
            </div>
          ))}
          <button type="button" className="add-option-btn" onClick={addOption}>
            <IconPlus /> Add Option
          </button>
        </div>
      )}

      {/* Rating config for rating scale */}
      {needsRatingConfig && (
        <div className="rating-config">
          <label className="options-label">Rating Scale Config</label>
          <div className="rating-config-row">
            <div className="form-group small">
              <label>Min</label>
              <input
                type="number"
                min={1}
                max={9}
                value={question.ratingConfig?.min || 1}
                onChange={(e) => handleRatingConfigChange("min", Math.max(1, Math.min(9, parseInt(e.target.value) || 1)))}
              />
            </div>
            <div className="form-group small">
              <label>Max</label>
              <input
                type="number"
                min={2}
                max={10}
                value={question.ratingConfig?.max || 5}
                onChange={(e) => handleRatingConfigChange("max", Math.max(2, Math.min(10, parseInt(e.target.value) || 5)))}
              />
            </div>
            <div className="form-group">
              <label>Min Label</label>
              <input
                type="text"
                placeholder="e.g., Poor"
                value={question.ratingConfig?.minLabel || ""}
                onChange={(e) => handleRatingConfigChange("minLabel", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Max Label</label>
              <input
                type="text"
                placeholder="e.g., Excellent"
                value={question.ratingConfig?.maxLabel || ""}
                onChange={(e) => handleRatingConfigChange("maxLabel", e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Action selection for rating scale questions */}
      {needsActionSelect && (
        <div className="action-select-section">
          <label className="options-label">
            Link to Action <span className="required">*</span>
          </label>
          <p className="action-select-help">
            Select which competency action this rating will measure. Results will be grouped by action.
          </p>
          {actionsLoading ? (
            <div className="action-loading">Loading actions...</div>
          ) : actions.length === 0 ? (
            <div className="action-empty">
              No actions found. Please ensure the company has a competency set assigned.
            </div>
          ) : (
            <ActionPicker
              value={question.linkedAction?.actionId || ""}
              actions={actions}
              groupedActions={groupedActions}
              onSelect={handleActionSelect}
            />
          )}
          {question.linkedAction && (
            <div className="action-preview">
              <strong>Selected:</strong> {question.linkedAction.valueName} → {question.linkedAction.competencyName} → {question.linkedAction.actionName}
              {question.linkedAction.entries?.length > 0 && (
                <div className="action-entries">
                  {question.linkedAction.entries.map((entry, i) => (
                    <span key={i} className="entry-tag">
                      {entry.skill && <span className="skill">{entry.skill}</span>}
                      {entry.attitude && <span className="attitude">{entry.attitude}</span>}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN MODAL COMPONENT
// ============================================
export default function DiagnosticTemplateFormModal({ template, companyId, onClose }) {
  // ============================================
  // STATE
  // ============================================
  const isEditing = !!template;
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    instructions: "",
    questions: [],
  });
  const [error, setError] = useState("");

  // ============================================
  // API HOOKS
  // ============================================
  const [createTemplate, { isLoading: isCreating }] = useCreateDiagnosticTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateDiagnosticTemplateMutation();
  const [publishTemplate, { isLoading: isPublishing }] = usePublishDiagnosticTemplateMutation();

  // Fetch actions for the company's competency set
  const { data: actions = [], isLoading: actionsLoading } = useGetActionsForCompanyQuery(
    companyId,
    { skip: !companyId }
  );

  const isSaving = isCreating || isUpdating || isPublishing;

  // ============================================
  // INITIALIZE FORM DATA
  // ============================================
  useEffect(() => {
    if (template) {
      setFormData({
        title: template.title || "",
        description: template.description || "",
        instructions: template.instructions || "",
        questions: (template.questions || []).map((q) => {
          const qData = q.questionId || q.inlineQuestion || q;
          const rawType = qData.type || qData.questionType || "text_short";
          return {
            questionType: normalizeQuestionType(rawType),
            questionText: qData.text || qData.questionText || "",
            isRequired: qData.validation?.required || qData.isRequired || false,
            options: qData.options || [],
            ratingConfig: qData.ratingConfig || { min: 1, max: 5 },
            linkedAction: qData.linkedAction || null,
          };
        }),
      });
    }
  }, [template]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuestionChange = (index, question) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = question;
    handleChange("questions", newQuestions);
  };

  const handleRemoveQuestion = (index) => {
    const newQuestions = [...formData.questions];
    newQuestions.splice(index, 1);
    handleChange("questions", newQuestions);
  };

  const handleAddQuestion = () => {
    handleChange("questions", [
      ...formData.questions,
      {
        questionType: "text_short",
        questionText: "",
        isRequired: false,
        options: [],
        ratingConfig: { min: 1, max: 5 },
        linkedAction: null,
      },
    ]);
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError("Title is required");
      return false;
    }
    if (formData.questions.length === 0) {
      setError("At least one question is required");
      return false;
    }
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (!q.questionText.trim()) {
        setError(`Question ${i + 1} text is required`);
        return false;
      }
      if (["single_choice", "multi_choice"].includes(q.questionType)) {
        if (!q.options || q.options.length < 2) {
          setError(`Question ${i + 1} needs at least 2 options`);
          return false;
        }
        for (let j = 0; j < q.options.length; j++) {
          if (!q.options[j].label.trim() || !q.options[j].value.trim()) {
            setError(`Question ${i + 1} option ${j + 1} needs both label and value`);
            return false;
          }
        }
      }
      if (q.questionType === "rating_scale") {
        const min = q.ratingConfig?.min || 1;
        const max = q.ratingConfig?.max || 5;
        if (min < 1 || min > 9) {
          setError(`Question ${i + 1} rating min must be between 1 and 9`);
          return false;
        }
        if (max < 2 || max > 10) {
          setError(`Question ${i + 1} rating max must be between 2 and 10`);
          return false;
        }
        if (min >= max) {
          setError(`Question ${i + 1} rating min must be less than max`);
          return false;
        }
        // Require linkedAction for rating_scale questions
        if (!q.linkedAction?.actionId) {
          setError(`Question ${i + 1} must be linked to a competency action`);
          return false;
        }
        // Validate action still exists in current list
        const actionExists = actions.some(a => a.actionId === q.linkedAction.actionId);
        if (!actionExists) {
          setError(`Question ${i + 1}: linked action no longer exists. Please reselect.`);
          return false;
        }
      }
    }
    setError("");
    return true;
  };

  const buildPayload = () => {
    return {
      companyId: companyId,
      title: formData.title.trim(),
      description: formData.description.trim(),
      instructions: formData.instructions.trim(),
      questions: formData.questions.map((q, index) => ({
        order: index,
        inlineQuestion: {
          type: q.questionType,
          text: q.questionText.trim(),
          validation: {
            required: q.isRequired || false,
          },
          options: ["single_choice", "multi_choice"].includes(q.questionType) && q.options?.length > 0
            ? q.options.map((opt, i) => ({ ...opt, order: i }))
            : undefined,
          ratingConfig: q.questionType === "rating_scale" ? q.ratingConfig : undefined,
          linkedAction: q.questionType === "rating_scale" && q.linkedAction ? q.linkedAction : undefined,
        },
      })),
    };
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    try {
      const payload = buildPayload();
      if (isEditing) {
        await updateTemplate({ id: template._id, ...payload }).unwrap();
      } else {
        await createTemplate(payload).unwrap();
      }
      onClose();
    } catch (err) {
      console.error("Save error:", err);
      setError(err.data?.message || "Failed to save template");
    }
  };

  const handlePublish = async () => {
    if (!validateForm()) return;

    try {
      const payload = buildPayload();
      let templateId = template?._id;

      if (isEditing) {
        await updateTemplate({ id: template._id, ...payload }).unwrap();
      } else {
        const result = await createTemplate(payload).unwrap();
        templateId = result.data?.template?._id || result.template?._id;
      }

      if (!templateId) {
        throw new Error("Failed to get template ID after creation");
      }

      await publishTemplate(templateId).unwrap();
      onClose();
    } catch (err) {
      console.error("Publish error:", err);
      setError(err.data?.message || err.message || "Failed to publish template");
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="template-modal-overlay" onClick={onClose}>
      <div className="template-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="template-modal-header">
          <h2>{isEditing ? "Edit Diagnostic Template" : "Create New Diagnostic Template"}</h2>
          <button className="close-btn" onClick={onClose}>
            <IconClose />
          </button>
        </div>

        {/* Body */}
        <div className="template-modal-body">
          {error && <div className="template-error">{error}</div>}

          {/* Basic Info */}
          <div className="form-section">
            <div className="form-group">
              <label>Title <span className="required">*</span></label>
              <input
                type="text"
                placeholder="Enter template title..."
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                placeholder="Brief description of this diagnostic template..."
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>Instructions</label>
              <textarea
                placeholder="Instructions shown to employees when filling out the form..."
                value={formData.instructions}
                onChange={(e) => handleChange("instructions", e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {/* Questions */}
          <div className="form-section questions-section">
            <div className="questions-header">
              <label className="section-label">Questions <span className="required">*</span></label>
              <span className="question-count">{formData.questions.length} question(s)</span>
            </div>

            {formData.questions.length === 0 && (
              <div className="no-questions">
                No questions added yet. Click below to add your first question.
              </div>
            )}

            {formData.questions.map((q, idx) => (
              <QuestionEditor
                key={idx}
                question={q}
                index={idx}
                onChange={handleQuestionChange}
                onRemove={handleRemoveQuestion}
                actions={actions}
                actionsLoading={actionsLoading}
              />
            ))}

            <button type="button" className="add-question-btn" onClick={handleAddQuestion}>
              <IconPlus /> Add Question
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="template-modal-footer">
          <button className="btn secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn secondary" onClick={handleSaveDraft} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save as Draft"}
          </button>
          <button className="btn primary" onClick={handlePublish} disabled={isSaving}>
            {isSaving ? "Publishing..." : "Save & Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
